import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-data-loader>` says about itself.
 *
 * The case worth knowing is the dependent choice. The external-identifier field exists only for an
 * upsert and has no options until an object is chosen, and both obvious answers to that are wrong: a
 * hidden control makes the screen change shape under somebody and hides that the choice exists; a
 * disabled one says "not for you" rather than "not yet". So it is present, empty, with its prompt
 * readable, saying what it is waiting for. That is the dependent dropdown the platform's own picker
 * could not express.
 *
 * Mapping is PROPOSED, never imposed: columns are matched to fields by name, every proposal is a
 * control the person can change, and anything with no confident match is proposed as "do not load
 * this column" rather than guessed at — a wrong mapping nobody notices writes the wrong data into
 * the wrong field.
 */
const docs: ComponentDocs = {
  aliases: ['bulk load', 'import wizard', 'csv import', 'data import', 'mass update'],

  answers: [
    'load a file of records into an object',
    'map file columns onto fields',
    'upsert against an external identifier',
    'find out which rows of an import failed and why',
  ],

  placements: ['appPage'],

  whenToUse: 'When records are being written in bulk from a file and somebody has to describe the load first.',

  insteadUse: [
    { name: 'FileField', when: 'a file is being attached to a record rather than loaded as records.' },
    {
      name: 'ResultGrid',
      when: 'the rows are being READ rather than written — the loader’s own result table is about outcomes.',
    },
  ],

  inputs: [
    {
      name: 'action',
      attribute: 'action',
      type: 'text',
      options: ['insert', 'upsert', 'update', 'delete'],
      description:
        'What the load does. `upsert` reveals the external-identifier choice; `delete` replaces the ' +
        'mapping section entirely, because a delete is driven by ids and an unused mapping section ' +
        'invites somebody to wonder what they got wrong.',
      default: 'insert',
    },
    {
      name: 'sizing',
      attribute: 'sizing',
      type: 'text',
      options: ['batch', 'standard'],
      description: 'The bulk path or the synchronous one.',
      default: 'batch',
    },
    {
      name: 'object',
      attribute: 'object',
      type: 'objectRef',
      description:
        'Which object is being loaded onto. Changing it RE-PROPOSES the mapping — a different object ' +
        'has different fields, and keeping the old proposals would carry a mapping nobody chose.',
    },
    {
      name: 'externalId',
      attribute: 'external-id',
      type: 'fieldRef',
      description:
        'Which field identifies an existing record, for an upsert. Its options come from ' +
        '`externalIds`, which is empty until an object is chosen — and that emptiness is SHOWN.',
    },
    {
      name: 'objects',
      type: 'json',
      description: 'The objects that can be loaded onto.',
      default: [],
    },
    {
      name: 'columns',
      type: 'json',
      description: 'The file’s columns, in file order. Setting them re-proposes the mapping.',
      default: [],
    },
    {
      name: 'fields',
      type: 'json',
      description: 'The fields of the chosen object, which the columns map onto.',
      default: [],
    },
    {
      name: 'externalIds',
      type: 'json',
      description:
        'The fields that can serve as an external identifier. EMPTY until an object is chosen, which ' +
        'the control says rather than hides.',
      default: [],
    },
    {
      name: 'mapping',
      type: 'json',
      description:
        'Column → field. A column mapped to the empty string is deliberately NOT loaded, which is a ' +
        'real answer with its own words in the control rather than a blank option.',
    },
    {
      name: 'results',
      type: 'json',
      description:
        'What each row did: `{ row, ok, id?, error? }[]`. `row` is the row’s number IN THE FILE — the ' +
        'only thing connecting an error back to the file on somebody’s disk.',
      default: [],
    },
    {
      name: 'running',
      attribute: 'running',
      type: 'boolean',
      description: 'A load is in flight.',
      default: false,
    },
    {
      name: 'startLabel',
      attribute: 'start-label',
      type: 'text',
      description: 'What the start control is called.',
      default: 'Start load',
    },
  ],

  events: [
    {
      name: 'caos-load-start',
      when: 'The start control is pressed, with the load described completely enough.',
      carries:
        '`{ action, sizing, object, externalId?, mapping }` — the mapping holds ONLY the columns going ' +
        'somewhere, so the caller does not re-apply a decision already recorded.',
    },
    { name: 'caos-load-change', when: 'Action, object or sizing changes.', carries: '`{ action, sizing, object }`.' },
    { name: 'caos-load-mapping', when: 'One column’s field changes.', carries: '`{ column, field, mapping }`.' },
  ],

  state: [
    {
      name: 'ready',
      type: 'boolean',
      description:
        'Whether the load is described completely enough to ask for: an object, some columns, an ' +
        'external identifier for an upsert, and — for anything but a delete — at least one column ' +
        'actually going somewhere.',
    },
    { name: 'mapping', type: '{ [column]: string }', description: 'The current mapping, skips included.' },
    { name: 'failures', type: 'CaosLoaderRowResult[]', description: 'The rows that failed, keeping their file row numbers.' },
  ],

  parts: [
    { part: 'loader', is: 'The outer column.' },
    { part: 'settings', is: 'The action / object / sizing row.' },
    { part: 'field', is: 'One setting and its label.' },
    { part: 'mapping', is: 'The column-to-field section.' },
    { part: 'mapping-row', is: 'One column’s mapping row.', when: 'The action is not `delete` and there are columns.' },
    { part: 'summary', is: 'The mapped-column count, or the per-row outcome counts.' },
    { part: 'start', is: 'The start control.' },
    { part: 'results', is: 'The per-row result table.', when: 'Results have been supplied.' },
  ],

  accessibility:
    'Every setting and every mapping control carries its own accessible name — "Field for the column ' +
    'Account Name" — so a screen of dropdowns is navigable rather than a row of unlabelled boxes. ' +
    'The waiting external-identifier control keeps full contrast and states what it is waiting for ' +
    'in its own prompt, so "not yet" and "not for you" are never confused. The summary is a ' +
    '`role="status"` live region. A skipped column says "Do not load this column" in words rather ' +
    'than being represented by a blank option.',

  examples: [
    {
      key: 'upsert',
      label: 'An upsert waiting for its object',
      summary:
        'The dependent choice: the external-identifier control is present and empty, with its prompt ' +
        'saying what it is waiting for — not hidden, and not disabled.',
      placement: {
        component: 'caos-data-loader',
        inputs: {
          action: 'upsert',
          objects: ['Account', 'Contact', 'Invoice__c'],
          columns: ['Account Name', 'Industry', 'Legacy Code', 'Notes'],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'mapped',
      label: 'A mapping proposed and corrected',
      summary:
        'Three columns matched by name, one with no confident match proposed as "do not load" rather ' +
        'than guessed at — a wrong mapping nobody notices becomes their data.',
      placement: {
        component: 'caos-data-loader',
        inputs: {
          action: 'insert',
          object: 'Account',
          objects: ['Account', 'Contact'],
          fields: ['Name', 'Industry', 'AnnualRevenue', 'Website'],
          columns: ['Name', 'Industry', 'Annual Revenue', 'Internal Ref'],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'results',
      label: 'What each row did',
      summary:
        'Failures first, each keeping its own row number — "row 412 failed" is what connects an ' +
        'error back to the file on somebody’s disk.',
      placement: {
        component: 'caos-data-loader',
        inputs: {
          action: 'insert',
          object: 'Account',
          objects: ['Account'],
          fields: ['Name'],
          columns: ['Name'],
          results: [
            { row: 1, ok: true, id: '001x0001' },
            { row: 2, ok: true, id: '001x0002' },
            { row: 412, ok: false, error: 'Required field missing: Industry' },
            { row: 998, ok: false, error: 'Duplicate value on a unique field' },
          ],
        },
      },
      stage: { needsWidth: 680 },
    },
  ],
};

export default docs;
